import { Input } from '@/src/common/components/Input/Input'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import type { TextInput } from 'react-native'
import type { DatePickerType } from '@/src/common/components/DatePicker/DatePickerType'
import { formatDateToShortString } from '@/src/common/utils/date'

/**
 * this component is for [the web _date_ picker](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/date),
 * not [the _datetime_ picker](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local)
 *
 * without time, adjusting the date for timezone differences just doesn't make much sense, so assume UTC
 */
const formatter = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC' })

export const DatePicker: DatePickerType = forwardRef(
  (
    {
      date,
      onDateChange,
      modalTitle,
      placeholder = 'Select a Date',
      errorMessage,
      inputProps,
      minimumDate,
      maximumDate,
    },
    ref,
  ) => {
    const inputRef = useRef<TextInput>(null)
    const datePickerRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => ({ open: () => datePickerRef.current?.showPicker() }))

    return (
      <>
        <Input
          placeholder={placeholder}
          ref={inputRef}
          onFocus={() => {
            datePickerRef.current?.showPicker()
            inputRef.current?.blur()
          }}
          value={date ? formatDateToShortString(date) : date}
          info={{ error: errorMessage }}
          {...inputProps}
        />
        <input
          type="date"
          ref={datePickerRef}
          title={modalTitle}
          value={(date || new Date()).toISOString().split('T')[0]}
          onChange={(e) => onDateChange(new Date(formatter.format(new Date(e.target.value))))}
          min={minimumDate?.toISOString().split('T')[0]}
          max={maximumDate?.toISOString().split('T')[0]}
          style={{ height: 0, border: 'none' }}
        />
      </>
    )
  },
)
